import {
  useEditMyEmailMutation,
  useEditMyPasswordMutation,
  usePutPartnerMutation,
} from '@/api/api.slice.tsx';
import { PartnerType, PartnerUpdateType } from '@/api/types.ts';
import { setIsLogged } from '@/app/Application.slice.ts';
import { useAppDispatch } from '@/app/hooks.ts';
import { Field } from '@/features/PartnerInfo/fields/Field.tsx';
import {
  Alert,
  Editable,
  EditableEmailTextField,
  EditablePasswordTextField,
  EditablePhoneInput,
  EditableTextField,
  getMessageFromError,
} from '@arsnovasystems/ars-front-lib';
import { Stack } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
/**
 * AddressFields
 *
 * @returns A component to display partner address fields
 */
export const PartnerFields = ({ me }: { me: PartnerType }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [handleUpdatePartner, handleUpdatePartnerResult] =
    usePutPartnerMutation();
  const [handleEditEmail, handleEditEmailResult] = useEditMyEmailMutation();

  const [handleEditPassword, handleEditPasswordResult] =
    useEditMyPasswordMutation();

  const handleSave = useCallback(
    async (newValue: Partial<PartnerUpdateType>) => {
      const partnerToSave: PartnerUpdateType = {
        companyVatNumber: newValue.companyVatNumber ?? me.companyVatNumber,
        address: newValue.address ?? me.address,
        stateRegion: newValue.stateRegion ?? me.stateRegion,
        town: newValue.town ?? me.town,
        zipCode: newValue.zipCode ?? me.zipCode,
        accountingEmail: newValue.accountingEmail ?? me.accountingEmail,
        firstName: newValue.firstName ?? me.firstName,
        lastName: newValue.lastName ?? me.lastName,
        phoneNumber: newValue.phoneNumber ?? me.phoneNumber,
        name: newValue.name ?? me.name,
      };

      const mutationResult = await handleUpdatePartner(partnerToSave);
      return mutationResult;
    },
    [handleUpdatePartner, me],
  );

  return (
    <Stack
      sx={{
        marginTop: 3,
      }}
    >
      <Field
        label={t('ik.email')}
        EditablePart={
          <Editable
            label={me.email}
            render={({ close }) => {
              return (
                <>
                  <EditableEmailTextField
                    label={t('ik.email')}
                    defaultValue={me.email}
                    onChange={async (updatedEmail) => {
                      const result = await handleEditEmail(updatedEmail);
                      if (result.error === undefined) {
                        close();
                        dispatch(setIsLogged(false));
                        setTimeout(() => {
                          window.location.reload();
                        }, 100);
                      }
                    }}
                  />
                  {handleEditEmailResult.error ?
                    <Alert>
                      {getMessageFromError(handleEditEmailResult.error)}
                    </Alert>
                  : null}
                </>
              );
            }}
          />
        }
      />
      <Field
        label={t('ik.password')}
        EditablePart={
          <Editable
            label={'******'}
            render={({ close }) => {
              return (
                <>
                  <EditablePasswordTextField
                    label={t('ik.password')}
                    onChange={async (updatedPassword: string) => {
                      const result = await handleEditPassword({
                        password: updatedPassword,
                      });
                      if (result.error === undefined) {
                        close();
                        dispatch(setIsLogged(false));
                        setTimeout(() => {
                          window.location.reload();
                        }, 100);
                      }
                    }}
                  />
                  {handleEditPasswordResult.error ?
                    <Alert>
                      {getMessageFromError(handleEditPasswordResult.error)}
                    </Alert>
                  : null}
                </>
              );
            }}
          />
        }
      />
      <Field
        label={t('ik.first.name')}
        EditablePart={
          <Editable
            label={me.firstName}
            render={({ close }) => {
              return (
                <>
                  <EditableTextField
                    required
                    label={t('ik.first.name')}
                    defaultValue={me.firstName}
                    onChange={async (updatedFirstname: string) => {
                      const result = await handleSave({
                        firstName: updatedFirstname,
                      });
                      if (result.error === undefined) {
                        close();
                      }
                    }}
                  />
                </>
              );
            }}
          />
        }
      />
      <Field
        label={t('ik.last.name')}
        EditablePart={
          <Editable
            label={me.lastName}
            render={({ close }) => {
              return (
                <>
                  <EditableTextField
                    required
                    label={t('ik.last.name')}
                    defaultValue={me.lastName}
                    onChange={async (updatedString: string) => {
                      const result = await handleSave({
                        lastName: updatedString,
                      });
                      if (result.error === undefined) {
                        close();
                      }
                    }}
                  />
                </>
              );
            }}
          />
        }
      />
      <Field
        label={t('ik.phone.number')}
        EditablePart={
          <Editable
            label={me.phoneNumber}
            render={({ close }) => {
              return (
                <EditablePhoneInput
                  defaultValue={'+' + me.phoneNumber}
                  onChange={async (updatedPhoneNumber) => {
                    const result = await handleSave({
                      phoneNumber: updatedPhoneNumber,
                    });

                    if (result.error === undefined) {
                      close();
                    }
                  }}
                />
              );
            }}
          />
        }
      />
      {handleUpdatePartnerResult.error ?
        <Alert>{getMessageFromError(handleUpdatePartnerResult.error)}</Alert>
      : null}
    </Stack>
  );
};
